import React from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Avatar,
  useTheme,
} from '@mui/material';
import { Menu, Bell, User } from 'lucide-react';
import { useMobile } from '../../../hooks/useMobile';
import { useMobileNavigation } from '../../../contexts/MobileNavigationContext';
import { mobileTokens } from '../../../theme/mobileTokens';

/**
 * MobileTopBar Props
 */
interface MobileTopBarProps {
  /** Number of unread notifications (0 hides badge) */
  notificationCount?: number;
  /** User's full name (for avatar initials) */
  userName?: string;
  /** Callback when notification bell is clicked */
  onNotificationClick?: () => void;
  /** Callback when user avatar is clicked */
  onAvatarClick?: () => void;
}

/**
 * Extract initials from user's full name
 */
const getInitials = (name: string): string => {
  if (!name) return '';

  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  // First and last name initials
  return (
    parts[0].charAt(0).toUpperCase() +
    parts[parts.length - 1].charAt(0).toUpperCase()
  );
};

/**
 * MobileTopBar Component
 *
 * Sticky mobile app bar with navigation controls and user actions.
 * Displays at top of viewport on mobile devices only (<768px).
 *
 * Features:
 * - Hamburger menu (opens MobileDrawer)
 * - App logo/title
 * - Notification bell with badge
 * - User avatar
 * - iOS safe area support
 * - Material Design styling
 * - Touch-optimized (44px minimum)
 *
 * @example
 * ```tsx
 * <MobileTopBar
 *   notificationCount={5}
 *   userName="John Doe"
 *   onNotificationClick={() => console.log('Notifications clicked')}
 *   onAvatarClick={() => console.log('Avatar clicked')}
 * />
 * ```
 */
export const MobileTopBar: React.FC<MobileTopBarProps> = ({
  notificationCount = 0,
  userName = '',
  onNotificationClick,
  onAvatarClick,
}) => {
  const theme = useTheme();
  const { isMobile } = useMobile();
  const { toggleDrawer } = useMobileNavigation();

  // Don't render on desktop/tablet
  if (!isMobile) {
    return null;
  }

  // Format notification badge content
  const getBadgeContent = (): string | number | undefined => {
    if (notificationCount <= 0) return undefined;
    if (notificationCount > 99) return '99+';
    return notificationCount;
  };

  // Get user initials for avatar
  const initials = getInitials(userName);

  return (
    <AppBar
      position="fixed"
      elevation={4}
      component="header"
      role="banner"
      sx={{
        top: 0,
        left: 0,
        right: 0,
        zIndex: mobileTokens.zIndex.fixed,
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <Toolbar
        sx={{
          minHeight: `${mobileTokens.topBar.height}px !important`,
          height: `${mobileTokens.topBar.height}px`,
          paddingX: theme.spacing(2),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: theme.spacing(1),
        }}
      >
        {/* Hamburger Menu Button */}
        <IconButton
          edge="start"
          color="inherit"
          aria-label="Ouvrir le menu"
          onClick={toggleDrawer}
          sx={{
            minWidth: `${mobileTokens.touchTargets.small}px`,
            minHeight: `${mobileTokens.touchTargets.small}px`,
          }}
        >
          <Menu size={mobileTokens.topBar.iconSize} />
        </IconButton>

        {/* App Logo/Title */}
        <Typography
          variant="h6"
          component="div"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            fontSize: '18px',
            marginLeft: theme.spacing(1),
          }}
        >
          Nexera
        </Typography>

        {/* Notification Bell */}
        <IconButton
          color="inherit"
          aria-label="Notifications"
          onClick={onNotificationClick}
          sx={{
            minWidth: `${mobileTokens.touchTargets.small}px`,
            minHeight: `${mobileTokens.touchTargets.small}px`,
          }}
        >
          <Badge
            badgeContent={getBadgeContent()}
            color="error"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '11px',
                height: '18px',
                minWidth: '18px',
                padding: '0 4px',
              },
            }}
          >
            <Bell size={mobileTokens.topBar.iconSize} />
          </Badge>
        </IconButton>

        {/* User Avatar */}
        <IconButton
          edge="end"
          color="inherit"
          aria-label="Profil utilisateur"
          onClick={onAvatarClick}
          sx={{
            padding: 0,
            minWidth: `${mobileTokens.touchTargets.small}px`,
            minHeight: `${mobileTokens.touchTargets.small}px`,
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '14px',
              bgcolor: theme.palette.secondary.main,
            }}
          >
            {initials || <User size={18} />}
          </Avatar>
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};
